import { useEffect, useState } from 'react';
import { PiWarningCircleLight } from 'react-icons/pi';
import { BsTicketPerforated } from 'react-icons/bs';
import FilterTable, { BaseData } from './Table/Table';
import {
  useDeleteOrderMutation, useUpdateOrderStatusMutation, useGetEventResponsesByOrderIdMutation,
} from '../store';
import IconInfoCard from './atoms/IconInfoCard';
import Button from './atoms/Button';
import CustomModal from './atoms/CustomModal';
import EmptyTable from './Table/EmptyTable';
import OrderItem from '../types/OrderItem';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import { PollResponse } from '../models/poll';
import Input from './atoms/Input';
import { formatCurrency } from '../utils/shared';

interface TableRow extends BaseData {
  displayId: string | JSX.Element;
  name: string | JSX.Element;
}

interface Props {
  orders: OrderItem[];
  eventId: string;
  businessId: string;
}

const PendingOrdersTable: React.FC<Props> = ({ orders, eventId, businessId }) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [updateOrderStatus, resultsUpdate] = useUpdateOrderStatusMutation();
  const [deleteOrder, resultsDelete] = useDeleteOrderMutation();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'reject' | 'accept' | 'responses'>('reject');
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [getEventResponsesByOrderId, { isLoading: IsLoadingEventResponses }] = useGetEventResponsesByOrderIdMutation();
  const [eventResponses, setEventResponses] = useState<PollResponse[] | null>(null);
  const { event } = useGetSelectedEvent();

  const getEventResponses = async (orderId: string) => {
    const response = await getEventResponsesByOrderId({ eventId, businessId, orderId });
    console.log(response);
    if ('data' in response) {
      setEventResponses(response.data);
    }
  };

  useEffect(() => {
    const pendingOrders = orders.filter((o) => o.order_status === 'pending');

    // Group by order_id and keep only one ticket per order
    const uniqueOrdersMap = new Map<string, OrderItem>();
    pendingOrders.forEach((order) => {
      if (!uniqueOrdersMap.has(order.order_id)) {
        uniqueOrdersMap.set(order.order_id, order);
      }
    });

    setOrderItems(Array.from(uniqueOrdersMap.values()));
  }, [orders]);

  const tableRows: TableRow[] = orderItems
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .map((item) => ({
      id: item.id,
      displayId: <div className='whitespace-nowrap text-text-primary'>{item.order_display_id}</div>,
      name: item.name,
      email: item.email,
      order_total: <div className='whitespace-nowrap text-text-primary'>{formatCurrency(item.order_total)}</div>,
    }));

  const handleSelectItem = (selectedIds: readonly string[]) => {
    setSelectedItems([...selectedIds]);
  };

  const handleConfirmRejectOrders = () => {
    selectedItems.map((id) => {
      deleteOrder({
        businessId,
        eventId,
        orderId: id,
      });
      return setSelectedItems(selectedItems.filter((p) => p !== id));
    });
    setShowModal(false);
  };

  const handleRejectOrders = () => {
    setModalType('reject');
    setShowModal(true);
  };

  const handleConfirmAcceptOrders = () => {
    selectedItems.map(async (id) => {
      await updateOrderStatus({
        businessId,
        eventId,
        orderId: id,
        newStatus: 'paid',
      });
      return setSelectedItems(selectedItems.filter((p) => p !== id));
    });
    setShowModal(false);
  };

  const handleAcceptOrders = () => {
    if (selectedItems.length === 1 && event?.has_polls) {
      getEventResponses(selectedItems[0]);
      setModalType('responses');
    } else {
      setModalType('accept');
    }
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const headCells = [
    {
      id: 'displayId',
      label: 'ID',
    },
    {
      id: 'name',
      label: 'Nombre',
    },
    {
      id: 'order_total',
      label: 'Monto',
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {orders.length > 0 ? (
        <>
          <FilterTable
            rows={tableRows}
            headCells={headCells}
            pagination={{ enabled: true, pageSize: 10 }}
            searchEnabled={true}
            allowSelect={true}
            onSelectionChange={handleSelectItem}
            tableId="nto-orders-table"
          />
          <div className="flex gap-4 w-full">
            <Button
              loading={resultsDelete.isLoading}
              disabled={selectedItems.length === 0}
              variant="secondary"
              onClick={handleRejectOrders}
            >
              Rechazar
            </Button>
            <Button
              loading={resultsUpdate.isLoading}
              disabled={selectedItems.length === 0}
              variant="primary"
              onClick={handleAcceptOrders}
              className="mr-4"
            >
              Aceptar
            </Button>
          </div>
        </>
      ) : (
        <EmptyTable
          title="Aún no hay solicitudes de pago"
          info="Aquí aparecerán las solicitudes de pago que se han realizado"
          icon={BsTicketPerforated}
          iconColor="text-secondary-500"
        />
      )}
      <CustomModal
        onRequestClose={handleClose}
        isOpen={showModal}
        hideCancelButton
      >
        {modalType === 'reject' && (
          <div>
            <IconInfoCard
              iconColor="text-primary-500"
              icon={PiWarningCircleLight}
              title="¿Deseas rechazar la solicitud?"
              info="Una vez rechazada desaparecerá de la lista de solicitudes."
              glass={false}
            />
            <div className="flex flex-row w-full gap-4 mt-4">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmRejectOrders}
              >
                Rechazar
              </Button>
            </div>
          </div>
        )}
        {modalType === 'accept' && (
          <div className="flex flex-col gap-2">
            <h1>Aceptar solicitud</h1>
            <p className="max-w-sm">Esta acción confirmará la/las solicitud/es de pago. Una vez confirmada una solicitud, podrás gestionarla desde la pestaña "Entradas confirmadas"</p>
            <div className="flex flex-col mt-4">
              <div className="flex flex-row w-full gap-4">
                <Button
                  variant="secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmAcceptOrders}
                >
                  Aceptar
                </Button>
              </div>
            </div>
          </div>
        )}
        {modalType === 'responses' && (
          <div className="flex flex-col gap-2 min-w-[60vw] lg:min-w-[40vw]">
            <h1>Respuestas</h1>
            {IsLoadingEventResponses && <div>Cargando...</div>}
            {!IsLoadingEventResponses && eventResponses && eventResponses.map((response) => (
              <div key={response.id} className="flex flex-col gap-2">
                <Input id="name" label="Nombre" labelAbove value={response.name} disabled />
                <Input id="last_name" label="Apellido" labelAbove value={response.last_name} disabled />
                <Input id="person_id" label="Documento" labelAbove value={response.person_id} disabled />
                {response.question_responses.map((question) => (
                  <div key={question.question_id}>
                    <Input id={question.question_id} label={question.question_text} labelAbove value={question.value} disabled />
                  </div>
                ))}
              </div>
            ))}
            <div className="flex flex-row w-full gap-4">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmAcceptOrders}
              >
                Aceptar
              </Button>
            </div>
          </div>
        )}
      </CustomModal>
    </div >
  );
};

export default PendingOrdersTable;
